import { Box, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { httpClient } from '../../../../api/http/http-client';
import { themePalette } from '../../../../components/ThemeProvider/themeColors';
import usePermissions from '../../../../store/auth/usePermissions';
import EmptyState from '../../../../shared/ui/elements/EmptyState';
import { useCustomNavigate } from '../../../../utils/customHooks/useCustomNavigate';
import ApplicationCard from './components/ApplicationCard';
import {
  EmployeeStatusesResponse,
  SalaryCardOrder,
  SalaryCardStatusesResponse,
} from './model/types';
import StatusesModal from './statuses-modal';

const ApplicationsPage = () => {
  const { t } = useTranslation('salary-card');
  const [selectedOrder, setSelectedOrder] = useState<SalaryCardOrder | null>(null);

  const navigate = useCustomNavigate();
  const { permissions } = usePermissions();
  const hasAccess = permissions['salary-card']?.salary_cards_statuses;

  const { data, isLoading } = useQuery({
    queryKey: ['salary-card-statuses'],
    queryFn: async (): Promise<SalaryCardStatusesResponse> =>
      await httpClient.get('/api-web/v1/main/salary_cards_statuses'),
    onSuccess: (response: SalaryCardStatusesResponse) => {
      navigate(`salary-card/applications?business_key=${response.business_key}`);
    },
    enabled: hasAccess,
  });

  if (!hasAccess) {
    return <EmptyState />;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data?.orders?.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: 4,
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography fontSize={14} color={themePalette.text_and_icon.third_party}>
          {t('noApplications')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.orders.map(order => (
          <ApplicationCard
            key={order.request_id}
            orderCount={order.order_count}
            requestDate={order.request_date}
            onViewStatuses={() => setSelectedOrder(order)}
          />
        ))}
      </Box>

      {selectedOrder && (
        <StatusesContent order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </>
  );
};

const StatusesContent = ({ order, onClose }: { order: SalaryCardOrder; onClose: () => void }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const business_key = searchParams.get('business_key');

  const { data, isLoading } = useQuery({
    queryKey: ['salary-card-employee-statuses', order.request_id],
    queryFn: async (): Promise<EmployeeStatusesResponse> => {
      try {
        return await httpClient.post(
          `/api-web/v1/main/salary_cards_statuses?business_key=${business_key}`,
          {
            view_state: {
              card_type: order.card_type,
              company_cif: order.company_cif,
              request_id: order.request_id,
            },
          }
        );
      } catch {
        return await httpClient.post(
          `/api-web/v1/main/salary_cards_statuses?business_key=${business_key}`,
          {
            view_state: {
              card_type: order.card_type,
              company_cif: order.company_cif,
              request_id: order.request_id,
            },
          }
        );
      }
    },
  });

  return (
    <StatusesModal
      open
      onClose={onClose}
      employees={data?.statuses_for_front}
      isLoading={isLoading}
    />
  );
};

export default ApplicationsPage;
